
import { useState, useEffect, useRef } from 'react';
import { motion, useMotionValue, useTransform } from 'framer-motion';

const ProductsHero = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const bgX = useTransform(mouseX, [0, 1], [-8, 8]);
  const bgY = useTransform(mouseY, [0, 1], [-8, 8]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [activeCategory, setActiveCategory] = useState(0);

  const categories = [
    { name: 'PET Şişe', icon: 'ri-drop-line', count: '50+' },
    { name: 'PET Kavanoz', icon: 'ri-archive-line', count: '30+' },
    { name: 'PET Preform', icon: 'ri-flask-line', count: '40+' },
    { name: 'PP Kapak', icon: 'ri-shield-check-line', count: '25+' },
  ];

  useEffect(() => {
    setIsLoaded(true);
    const interval = setInterval(() => {
      setActiveCategory((prev) => (prev + 1) % categories.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [categories.length]);

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    mouseX.set((e.clientX - rect.left) / rect.width);
    mouseY.set((e.clientY - rect.top) / rect.height);
  };

  return (
    <section
      ref={containerRef}
      onMouseMove={handleMouseMove}
      className="relative min-h-screen flex items-center overflow-hidden"
    >
      {/* Background with Parallax */}
      <motion.div style={{ x: bgX, y: bgY }} className="absolute inset-[-30px]">
        <img
          src="https://static.readdy.ai/image/07f91bd9f2ad3b84149b39a4e0627797/0f917293ec3336b2d83de5e73c517d4a.webp"
          alt="AnadoluPet Üretim Tesisi"
          className="w-full h-full object-cover object-top scale-110"
        />
      </motion.div>

      {/* Overlays */}
      <div className="absolute inset-0 bg-gradient-to-r from-navy/95 via-navy/85 to-navy/50" />
      <div className="absolute inset-0 bg-gradient-to-t from-navy/80 via-transparent to-navy/30" />

      {/* Animated Orbs */}
      <motion.div
        animate={{ x: [0, 20, -15, 0], y: [0, -15, 20, 0], scale: [1, 1.1, 0.95, 1] }}
        transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute top-[10%] right-[15%] w-80 h-80 bg-green/8 rounded-full blur-[140px]"
      />
      <motion.div
        animate={{ x: [0, -25, 15, 0], y: [0, 20, -15, 0], scale: [1, 0.9, 1.08, 1] }}
        transition={{ duration: 22, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute bottom-[15%] left-[5%] w-96 h-96 bg-teal/6 rounded-full blur-[160px]"
      />

      {/* Floating Geometric Shapes */}
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 80, repeat: Infinity, ease: 'linear' }}
        className="absolute top-[20%] right-[30%] w-64 h-64 border border-white/[0.03] rounded-full"
      />
      <motion.div
        animate={{ rotate: -360 }}
        transition={{ duration: 100, repeat: Infinity, ease: 'linear' }}
        className="absolute bottom-[25%] right-[20%] w-96 h-96 border border-white/[0.02] rounded-full"
      />

      {/* Main Content */}
      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 pt-28 sm:pt-32 pb-16 sm:pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center min-h-[calc(100vh-200px)]">
          {/* Left Content */}
          <div className="lg:col-span-7">
            {/* Breadcrumb */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={isLoaded ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="flex items-center gap-2 text-white/50 text-xs sm:text-sm mb-6 sm:mb-8"
            >
              <span className="hover:text-white/70 transition-colors cursor-pointer">Anasayfa</span>
              <i className="ri-arrow-right-s-line" />
              <span className="text-green font-medium">Ürünler</span>
            </motion.div>

            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isLoaded ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="mb-6 sm:mb-8"
            >
              <span className="inline-flex items-center gap-2.5 px-5 py-2.5 bg-white/10 border border-white/15 rounded-full backdrop-blur-md">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green" />
                </span>
                <span className="text-white/90 text-xs sm:text-sm font-medium tracking-wide">
                  4 Kategori &middot; 145+ Ürün Çeşidi
                </span>
              </span>
            </motion.div>

            {/* Title */}
            <motion.h1
              initial={{ opacity: 0, y: 40 }}
              animate={isLoaded ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="text-4xl sm:text-5xl md:text-6xl lg:text-6xl xl:text-7xl font-bold text-white leading-[1.08] mb-3"
            >
              Premium PET
            </motion.h1>
            <motion.h1
              initial={{ opacity: 0, y: 40 }}
              animate={isLoaded ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-4xl sm:text-5xl md:text-6xl lg:text-6xl xl:text-7xl font-bold leading-[1.08] mb-6 sm:mb-8"
            >
              <span className="bg-gradient-to-r from-green via-teal to-green bg-clip-text text-transparent">
                Ambalaj Çözümleri
              </span>
            </motion.h1>

            {/* Description */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={isLoaded ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.5 }}
              className="text-base sm:text-lg text-white/75 mb-8 sm:mb-10 max-w-xl leading-relaxed"
            >
              Gıda, içecek, kozmetik ve endüstriyel sektörlere özel, Avrupa standartlarında
              yüksek kaliteli PET ve PP ambalaj ürünleri.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isLoaded ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.6 }}
              className="flex flex-col sm:flex-row items-start gap-4 mb-12 sm:mb-16"
            >
              <a
                href="#urunler"
                className="group relative px-8 sm:px-10 py-3.5 sm:py-4 bg-gradient-to-r from-green to-teal text-white text-sm sm:text-base font-semibold rounded-lg overflow-hidden transition-all duration-500 shadow-lg shadow-green/25 hover:shadow-xl hover:shadow-green/35 hover:-translate-y-0.5 whitespace-nowrap cursor-pointer"
              >
                <span className="relative z-10 flex items-center gap-2">
                  Ürünleri Keşfet
                  <motion.i
                    animate={{ y: [0, 3, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
                    className="ri-arrow-down-line text-lg"
                  />
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-teal to-green opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </a>
              <a
                href="#teknik"
                className="group px-8 sm:px-10 py-3.5 sm:py-4 bg-white/5 border border-white/25 text-white text-sm sm:text-base font-semibold rounded-lg hover:bg-white/15 hover:border-white/40 hover:-translate-y-0.5 transition-all duration-500 whitespace-nowrap cursor-pointer backdrop-blur-sm"
              >
                <span className="flex items-center gap-2">
                  <i className="ri-file-list-3-line text-green" />
                  Teknik Bilgiler
                </span>
              </a>
            </motion.div>

            {/* Mini Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isLoaded ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.7 }}
              className="flex flex-wrap gap-6 sm:gap-8"
            >
              {[
                { value: '145+', label: 'Ürün Çeşidi' },
                { value: '4', label: 'Kategori' },
                { value: '20+', label: 'İhracat Ülkesi' },
              ].map((stat, i) => (
                <div key={i} className="text-center sm:text-left">
                  <div className="text-2xl sm:text-3xl font-bold text-white">{stat.value}</div>
                  <div className="text-white/50 text-xs sm:text-sm mt-1">{stat.label}</div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right Side - Animated Category Cards */}
          <div className="hidden lg:flex lg:col-span-5 relative items-center justify-center">
            <div className="relative w-full h-[520px]">
              {/* Central Glow */}
              <motion.div
                animate={{ scale: [1, 1.15, 1], opacity: [0.2, 0.35, 0.2] }}
                transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 bg-green/15 rounded-full blur-[100px]"
              />

              {/* Orbiting Ring */}
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 50, repeat: Infinity, ease: 'linear' }}
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-72 border border-dashed border-white/[0.06] rounded-full"
              />

              {/* Category Cards */}
              {categories.map((cat, index) => {
                const positions = [
                  { x: 'left-[10%]', y: 'top-[8%]' },
                  { x: 'right-[5%]', y: 'top-[18%]' },
                  { x: 'right-[10%]', y: 'bottom-[22%]' },
                  { x: 'left-[5%]', y: 'bottom-[12%]' },
                ];
                const pos = positions[index];
                const isActive = activeCategory === index;

                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.7 }}
                    animate={isLoaded ? {
                      opacity: 1,
                      scale: isActive ? 1.08 : 1,
                      y: [0, -6, 0],
                    } : {}}
                    transition={{
                      opacity: { duration: 0.6, delay: 0.4 + index * 0.15 },
                      scale: { duration: 0.5 },
                      y: { duration: 4 + index * 0.8, repeat: Infinity, ease: 'easeInOut' },
                    }}
                    onClick={() => setActiveCategory(index)}
                    className={`absolute ${pos.x} ${pos.y} cursor-pointer`}
                  >
                    <div
                      className={`relative bg-white/10 backdrop-blur-xl border rounded-2xl p-5 sm:p-6 transition-all duration-500 shadow-xl shadow-black/10 min-w-[170px] ${
                        isActive
                          ? 'border-green/50 bg-white/15'
                          : 'border-white/15 hover:border-white/30 hover:bg-white/12'
                      }`}
                    >
                      {/* Active Indicator */}
                      {isActive && (
                        <motion.div
                          layoutId="activeGlow"
                          className="absolute inset-0 rounded-2xl border-2 border-green/30"
                          transition={{ duration: 0.4 }}
                        />
                      )}

                      {/* Shimmer on active */}
                      {isActive && (
                        <div className="absolute inset-0 overflow-hidden rounded-2xl">
                          <motion.div
                            animate={{ x: ['-100%', '200%'] }}
                            transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
                            className="absolute top-0 left-0 w-1/3 h-full bg-gradient-to-r from-transparent via-white/10 to-transparent skew-x-12"
                          />
                        </div>
                      )}

                      <div className="relative">
                        <div
                          className={`w-12 h-12 flex items-center justify-center rounded-xl mb-3 transition-all duration-500 ${
                            isActive
                              ? 'bg-gradient-to-br from-green/40 to-teal/40 scale-110'
                              : 'bg-white/10'
                          }`}
                        >
                          <i
                            className={`${cat.icon} text-2xl ${
                              isActive ? 'text-green' : 'text-white/70'
                            }`}
                          />
                        </div>
                        <div className="text-white font-semibold text-sm mb-1">{cat.name}</div>
                        <div
                          className={`text-xs font-medium ${
                            isActive ? 'text-green' : 'text-white/40'
                          }`}
                        >
                          {cat.count} Çeşit
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}

              {/* Center Element */}
              <motion.div
                initial={{ opacity: 0, scale: 0.7 }}
                animate={isLoaded ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.8, delay: 0.9 }}
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
              >
                <motion.div
                  animate={{ rotate: [0, 5, -5, 0] }}
                  transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
                  className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-green to-teal rounded-2xl shadow-2xl shadow-green/30"
                >
                  <i className="ri-recycle-line text-white text-4xl" />
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Gradient Fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-navy/60 via-navy/20 to-transparent z-10" />

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.5 }}
        className="absolute bottom-12 left-1/2 -translate-x-1/2 z-20 hidden lg:flex flex-col items-center gap-2"
      >
        <motion.div
          animate={{ y: [0, 6, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          className="w-6 h-10 border-2 border-navy/20 rounded-full flex items-start justify-center p-1.5"
        >
          <motion.div
            animate={{ y: [0, 10, 0], opacity: [1, 0.3, 1] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
            className="w-1.5 h-1.5 bg-teal rounded-full"
          />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default ProductsHero;
